import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { categoriasConfig, categoriasReglas, categoriasMap, categoriasGlobalStats, getEnVivoPorCategoria, getRadioConMasCategorias, formatearNumeroCat, type CategoriaId } from '@/mocks/top10Categoria';

const catColoresAccent: Record<CategoriaId, string> = {
  regional: 'rose', pop: 'amber', rock: 'slate', cristiana: 'sky',
  noticias: 'emerald', deportes: 'orange', podcast: 'violet', talkshow: 'teal', educacion: 'emerald',
};

export default function Top10CategoriaSection() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<CategoriaId | 'reglas'>('regional');
  const radioEstrella = getRadioConMasCategorias();

  const isReglas = activeTab === 'reglas';
  const catConfig = !isReglas ? categoriasConfig.find(c => c.id === activeTab) : null;
  const entries = !isReglas ? categoriasMap[activeTab].slice(0, 5) : [];
  const podium = entries.slice(0, 3);
  const rest = entries.slice(3);
  const enVivoCount = !isReglas ? getEnVivoPorCategoria(activeTab).length : 0;

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold border border-amber-200 mb-4">
            <i className="ri-award-line text-xs"></i>
            COMPETENCIA ESPECIALIZADA
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-3">Top 10 por Categoría</h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Competencia por género y formato. Cada radio puede estar en varias categorías —{' '}
            <strong className="text-foreground-800">9 categorías, 90 posiciones, actualización automática.</strong>
          </p>
        </div>

        {/* Mini Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'Categorías', value: categoriasGlobalStats.totalCategorias, icon: 'ri-layout-grid-line', color: 'amber' },
            { label: 'Entradas', value: categoriasGlobalStats.totalEntradasRankeadas, icon: 'ri-stack-line', color: 'emerald' },
            { label: 'Más Competida', value: categoriasGlobalStats.categoriasConMasCompetencia, icon: 'ri-fire-line', color: 'rose' },
            { label: `${radioEstrella.entityName}`, value: `${radioEstrella.count} categorías`, icon: 'ri-star-line', color: 'violet' },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70">
              <div className={`w-9 h-9 rounded-lg bg-${s.color}-100 flex items-center justify-center`}>
                <i className={`${s.icon} text-${s.color}-600 text-sm`}></i>
              </div>
              <div>
                <p className="text-xs text-foreground-500">{s.label}</p>
                <p className="text-sm font-bold text-foreground-900">{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Category Tabs + Reglas */}
        <div className="flex flex-wrap items-center gap-2 mb-8">
          {categoriasConfig.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveTab(cat.id)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
                activeTab === cat.id
                  ? `${cat.bgLight} ${cat.color} border border-current/20`
                  : 'bg-background-100 text-foreground-500 hover:text-foreground-700 border border-background-200/60'
              }`}
            >
              <i className={`${cat.icon} text-[11px]`}></i>
              {cat.label}
            </button>
          ))}
          <button
            onClick={() => setActiveTab('reglas')}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
              isReglas
                ? 'bg-slate-100 text-slate-700 border border-slate-300'
                : 'bg-background-100 text-foreground-500 hover:text-foreground-700 border border-background-200/60'
            }`}
          >
            <i className="ri-scales-line text-[11px]"></i>
            Reglas
          </button>
        </div>

        {/* Content */}
        {isReglas ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {categoriasReglas.map(regla => (
              <div key={regla.id} className="p-6 rounded-2xl bg-background-100 border border-background-200/70">
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r ${regla.gradiente} mb-3`}>
                  <i className={`${regla.icono} text-xs`}></i>
                  Regla #{regla.id}
                </div>
                <h4 className="text-base font-bold text-foreground-900 mb-2">{regla.titulo}</h4>
                <p className="text-sm text-foreground-600 leading-relaxed">{regla.description}</p>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Category Description */}
            {catConfig && (
              <div className="flex items-start gap-4 p-5 rounded-2xl bg-background-100 border border-background-200/70 mb-6">
                <div className={`w-10 h-10 rounded-xl ${catConfig.bgLight} flex items-center justify-center shrink-0`}>
                  <i className={`${catConfig.icon} ${catConfig.color} text-lg`}></i>
                </div>
                <div>
                  <h4 className="text-base font-bold text-foreground-900">{catConfig.label} — {catConfig.totalRadios} radios en esta categoría</h4>
                  <p className="text-sm text-foreground-600 mt-1">{catConfig.description}</p>
                  {enVivoCount > 0 && (
                    <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                      {enVivoCount} en vivo ahora
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Podium Top 3 */}
            {podium.length >= 3 && (
              <div className="grid grid-cols-3 gap-4 mb-6">
                {/* #2 — Plata */}
                <div className="p-4 rounded-2xl bg-background-100 border border-background-200/70 text-center">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold mb-3">🥈 #2</span>
                  <img src={podium[1].logoUrl} alt={podium[1].entityName} className="w-14 h-14 rounded-xl object-cover mx-auto mb-2" loading="lazy" />
                  <p className="text-sm font-bold text-foreground-900 truncate">{podium[1].entityName}</p>
                  <p className="text-xs text-foreground-500 truncate">{podium[1].frecuencia}</p>
                  <p className="text-xs font-bold text-foreground-700 mt-1">{podium[1].scoreLabel}</p>
                  <span className={`text-[10px] font-semibold ${podium[1].scoreChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {podium[1].scoreChange >= 0 ? '+' : ''}{podium[1].scoreChange}
                  </span>
                </div>

                {/* #1 — Oro */}
                <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-center -mt-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-200 text-amber-800 text-[10px] font-bold mb-3">🥇 #1</span>
                  <img src={podium[0].logoUrl} alt={podium[0].entityName} className="w-16 h-16 rounded-xl object-cover mx-auto mb-2 ring-2 ring-amber-300" loading="lazy" />
                  <p className="text-sm font-bold text-foreground-900 truncate">{podium[0].entityName}</p>
                  <p className="text-xs text-foreground-500 truncate">{podium[0].frecuencia}</p>
                  <p className="text-base font-black text-amber-700 mt-1">{podium[0].scoreLabel}</p>
                  <span className="text-[10px] font-semibold text-emerald-600">+{podium[0].scoreChange}</span>
                  <div className="mt-1 flex items-center justify-center gap-1">
                    {podium[0].isLive && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 text-[9px] font-bold">
                        <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse"></span>EN VIVO
                      </span>
                    )}
                  </div>
                </div>

                {/* #3 — Bronce */}
                <div className="p-4 rounded-2xl bg-background-100 border border-background-200/70 text-center">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold mb-3">🥉 #3</span>
                  <img src={podium[2].logoUrl} alt={podium[2].entityName} className="w-14 h-14 rounded-xl object-cover mx-auto mb-2" loading="lazy" />
                  <p className="text-sm font-bold text-foreground-900 truncate">{podium[2].entityName}</p>
                  <p className="text-xs text-foreground-500 truncate">{podium[2].frecuencia}</p>
                  <p className="text-xs font-bold text-foreground-700 mt-1">{podium[2].scoreLabel}</p>
                  <span className={`text-[10px] font-semibold ${podium[2].scoreChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {podium[2].scoreChange >= 0 ? '+' : ''}{podium[2].scoreChange}
                  </span>
                </div>
              </div>
            )}

            {/* Rest 4-5 */}
            {rest.length > 0 && (
              <div className="grid grid-cols-2 gap-3 mb-6">
                {rest.map(entry => (
                  <div key={entry.entityName + entry.position} className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-background-200/70">
                    <span className="w-7 h-7 rounded-full bg-foreground-100 flex items-center justify-center text-xs font-black text-foreground-600 shrink-0">
                      {entry.position}
                    </span>
                    <img src={entry.logoUrl} alt={entry.entityName} className="w-9 h-9 rounded-lg object-cover shrink-0" loading="lazy" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground-900 truncate">{entry.entityName}</p>
                      <p className="text-xs text-foreground-500 truncate">{entry.frecuencia}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs font-bold text-foreground-700">{entry.scoreLabel}</p>
                      <span className={`text-[10px] font-semibold ${entry.scoreChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {entry.scoreChange >= 0 ? '+' : ''}{entry.scoreChange}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* CTA */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/top-10-categoria')}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold cursor-pointer whitespace-nowrap hover:opacity-90 transition-all"
          >
            <i className="ri-award-line"></i>
            Ver Top 10 por Categoría Completo
          </button>
        </div>
      </div>
    </section>
  );
}